import React, { ReactElement, Suspense } from 'react';

import { AttendeeMessageThread } from '$/components/Chat/AttendeeMessageThread';
import WebinarPlayer from '$/components/WebinarPlayer/WebinarPlayer';
import { HasJoined, PlayerMode, PlayerPlacement } from '$/components/WebinarPlayer/types';
import { withWebinarPlayerContext } from '$/components/WebinarPlayer/context';
import config from '$/config';
import { CookieBannerMode, ChatType, RoomType } from '$/GraphQL/schema/schema';
import ChatSocket from '$/components/Socket/ChatSocket';
import Socket from '$/components/Socket/Socket';
import { Router } from '@reach/router';
import { Redirect } from '$/utils/Redirect';
import { css } from '@emotion/react';
import { logger } from '$/utils/logger';
import WithRender from '$/utils/WithRender';
import { FBPixelContextProvider } from '$/utils/useFBPixel';
import ChatContextProvider from '$/components/Chat/ChatContext';
import Helmet from 'react-helmet';
import { GdprContext } from '$/components/GdprCookie/GdprContext';
import I18nLoader from '$/components/I18nLoader';
import { ViewProps } from './types';

import { useInactiveReplay } from '$/components/WebinarPlayer/useInactiveReplay';
import { useDetectNotSupportedBrowser } from './BlockUnsupportedBrowsers';
import SEO from '$/components/SEO';
import { getSocialRegistrationMedia } from '$/utils/seo';
import { PlaybackEventNotifier } from './PlaybackEventNotifier';
import { useHasChangedShallow } from '$/hooks/useHasChanged';
import { WaitFor } from '$/components/DelayRender';
import ConsoleWindow from '$/components/WebinarPlayer/ConsoleWindow';
import { PoweredByEWebinar } from '$/components/Logo';
import { AttendeeContextProvider } from './AttendeeContextProvider';
import { useAttendeeContext } from './AttendeeContext';

const ThirdPartyChat = React.lazy(() => import('$/components/Chat/ThirdPartyChat'));
const GdprBannerWidget = React.lazy(() =>
  import('$/sections/Home/WebinarEditor/RegistrationPages/RegistrationPagesWidget').then(
    ({ GdprBannerWidget }) => ({ default: GdprBannerWidget })
  )
);
interface SocketAttendeeProps {
  attendeeId: string;
  mode: PlayerMode;
  children?: React.ReactNode;
}

const SocketAttendee: React.FC<SocketAttendeeProps> = withWebinarPlayerContext(
  [
    'currentRoom',
    'hasJoined',
    'shortIdRef',
    'lastActiveDateRef',
    'isPlaying',
    'isReplayExpired',
    'inactiveReplay',
    'onRejoinReplay',
    'onInactiveReplay',
    'ewebinar',
    'pauseTimer',
    'attendee',
  ],
  'SocketAttendee'
)(({ children, attendeeId, mode, context }) => {
  const socketRef = React.useRef<ChatSocket>();

  // Do not init socket in PostExit Room or Inactive replay
  const {
    currentRoom,
    hasJoined,
    shortIdRef,
    lastActiveDateRef,
    isPlaying,
    isReplayExpired,
    inactiveReplay,
    onRejoinReplay,
    onInactiveReplay,
    ewebinar,
    pauseTimer,
  } = context;

  const { inactive: hasBeenInactiveFor15Min } = useInactiveReplay({
    lastActiveDateRef,
    isPlaying,
    mode,
    isReplayExpired,
    inactiveReplay,
    onRejoinReplay,
    onInactiveReplay,
    ewebinar,
    pauseTimer,
  });

  const onConnect = (socket: ChatSocket): void => {
    socketRef.current = socket;
    socket.send({
      attendee: attendeeId,
      isReplay: mode === PlayerMode.replay,
      shortId: shortIdRef.current,
    });
  };

  /**
   * Close socket if Replay is inactive in 15 mins
   */
  React.useEffect(() => {
    if (!socketRef.current || mode != PlayerMode.replay || isReplayExpired) return;

    if (hasBeenInactiveFor15Min) {
      console.info('Inactive replay, closing socket');
      socketRef.current.terminate();
    } else if (!socketRef.current.isConnected) {
      // reconnect socket as soon as attendee Rejoin replay
      socketRef.current.open();
    }
  }, [socketRef, hasBeenInactiveFor15Min, mode, isReplayExpired]);

  const hasChangedRoom = useHasChangedShallow([currentRoom.type, isReplayExpired]);
  React.useEffect(() => {
    if (!hasChangedRoom) return;

    if (socketRef.current && (currentRoom.type === RoomType.PostExit || isReplayExpired)) {
      socketRef.current.terminate();
    }
  }, [currentRoom.type, hasChangedRoom, isReplayExpired]);

  const isAnonymous = attendeeId === 'anonymous';

  const skipConnect =
    isAnonymous ||
    hasJoined !== HasJoined.Joined ||
    currentRoom.type === RoomType.PostExit ||
    isReplayExpired;
  return (
    <Socket uri={config.CHAT_SERVER_URL} onConnect={onConnect} skipConnect={skipConnect}>
      <>
        {children}
        {!isAnonymous && <PlaybackEventNotifier mode={mode} attendeeId={attendeeId} />}
      </>
    </Socket>
  );
});

const InnerWebinarPlayer: React.FC<{
  attendeeId: string;
  mode: PlayerMode;
  teamId: string;
  hideBranding?: boolean;
}> = withWebinarPlayerContext(
  ['dimensions', 'currentRoom', 'actionsPaneState', 'isCollapsedEmbeddedPlayer', 'chatType'],
  'InnerWebinarPlayer'
)(({ attendeeId, mode, teamId, hideBranding, context }) => {
  const { dimensions, actionsPaneState, isCollapsedEmbeddedPlayer, chatType } = context;

  const showBranding =
    actionsPaneState === 'shown' &&
    context.currentRoom.type !== RoomType.PostExit &&
    !isCollapsedEmbeddedPlayer;

  const messagesThread = React.useMemo(() => {
    if (chatType === ChatType.None) return null;
    if (chatType === ChatType.ThirdParty) return <ThirdPartyChat />;
    return <AttendeeMessageThread key='MessageThread' />;
  }, [chatType]);

  return (
    <SocketAttendee attendeeId={attendeeId} mode={mode}>
      <WebinarPlayer.WebinarArea>
        <WebinarPlayer.VideoEditControlsArea>
          <WebinarPlayer.VideoArea />
        </WebinarPlayer.VideoEditControlsArea>

        <WaitFor element='interactionStream'>
          <WebinarPlayer.ActionsPane key='ActionsPane'>
            <WebinarPlayer.InteractionsStream />
          </WebinarPlayer.ActionsPane>
        </WaitFor>
        <div
          css={css`
            position: absolute;
            bottom: ${[ChatType.None, ChatType.ThirdParty].includes(chatType as ChatType)
              ? '2rem'
              : `calc(${dimensions.chat.heightPx}px + 1rem)`};
            right: ${dimensions.actionsPane.idock.widthRem +
            dimensions.actionsPane.idock.paddingLeftRem}rem;
            width: ${dimensions.actionsPane.icard.widthRem}rem;
            transition:
              opacity 1s ease-in-out,
              visibility 1s ease-in-out;
            opacity: ${showBranding ? 1 : 0};
            visibility: ${showBranding ? 'visible' : 'hidden'};
          `}
        >
          <PoweredByEWebinar
            hideBranding={hideBranding}
            teamId={teamId}
            clickThroughIds={['cards-list', 'interaction-stream']}
          />
        </div>

        <WaitFor element='messagesThread'>{messagesThread}</WaitFor>
      </WebinarPlayer.WebinarArea>
    </SocketAttendee>
  );
});

export const AttendeeViewWebinar: React.FC<ViewProps> = ({
  setId,
  mode,
  placement = PlayerPlacement.EWebinarPage,
  isTest,
}) => {
  const { isAcceptedCookies } = React.useContext(GdprContext);
  const isReplay = mode === PlayerMode.replay;

  useDetectNotSupportedBrowser(placement === PlayerPlacement.EmbeddedPlayer);

  const { attendee } = useAttendeeContext();

  const webinarSEO = React.useMemo(() => {
    const publicWebinar = attendee?.ewebinar;
    const set = attendee?.set;
    if (!publicWebinar || !set) {
      return null;
    }

    return (
      <SEO
        {...getSocialRegistrationMedia(set)}
        url={isReplay ? set?.urls.retargetingReplay : undefined}
        titleTemplate={`%s | Webinar ${isReplay ? 'Replay' : 'View'}`}
        canonicalUrl={set?.urls.registration}
      />
    );
  }, [attendee?.ewebinar, attendee?.set, isReplay]);

  const pageContent = React.useMemo(() => {
    if (!attendee.ewebinar) {
      logger.error(`Attendee webinar ${attendee.ewebinar} not found`);
      return <Redirect to={`/webinar/${setId}`} />;
    }

    const ewebinar = attendee.ewebinar!;
    const set = attendee.set;
    const pixelID = set.setSettings.pixelID;

    return (
      <FBPixelContextProvider
        pixelID={pixelID}
        isAcceptedCookies={
          set.team.settings.gdpr.bannerMode === CookieBannerMode.Off || isAcceptedCookies
        }
      >
        <Router>
          <WithRender
            path='/*'
            render={(): ReactElement => (
              <I18nLoader langCode={ewebinar.viewerRoomSettings.language}>
                <Helmet>
                  <style>
                    {`body{
                      background-color: #fff !important;
                      overflow: hidden !important;
                    }
                    html{
                      overflow: hidden !important;
                    }
                  `}
                  </style>
                </Helmet>

                <WaitFor element='gdprBannerWidget'>
                  {placement === PlayerPlacement.EmbeddedPlayer && isAcceptedCookies ? null : (
                    <Suspense>
                      <GdprBannerWidget set={set} ewebinar={ewebinar} />
                    </Suspense>
                  )}
                </WaitFor>

                <ChatContextProvider attendee={attendee}>
                  <div
                    id='ViewWebinar'
                    css={css`
                      position: absolute;
                      top: 0;
                      bottom: 0;
                      left: 0;
                      right: 0;
                    `}
                  >
                    <WebinarPlayer
                      mode={mode}
                      ewebinar={ewebinar}
                      set={attendee.set}
                      placement={placement}
                      attendee={attendee}
                      inTestMode={isTest}
                    >
                      <InnerWebinarPlayer
                        attendeeId={attendee.id}
                        mode={mode}
                        teamId={ewebinar.team.id}
                        hideBranding={Boolean(ewebinar.team.settings.hideBranding)}
                      />
                    </WebinarPlayer>
                    <ConsoleWindow />
                  </div>
                </ChatContextProvider>
              </I18nLoader>
            )}
          />
        </Router>
      </FBPixelContextProvider>
    );
  }, [setId, attendee, isAcceptedCookies, placement, mode, isTest]);

  return (
    <>
      {webinarSEO}
      {pageContent}
    </>
  );
};

const ViewWebinar: React.FC<ViewProps> = (props) => {
  return (
    <AttendeeContextProvider {...props}>
      <AttendeeViewWebinar {...props} />
    </AttendeeContextProvider>
  );
};

export default ViewWebinar;
